import type { Dimensions } from '@festina-life/core'
import { useSvgTextSize } from '@festina-life/ui/hooks'
import { mergeRefs } from '@festina-life/ui/libs'
import { Group } from '@visx/group'
import type { MouseEvent, ReactElement, Ref, SVGAttributes } from 'react'
const RoundedBorderSize = {
  none: 0,
  sm: '3',
  md: '5',
  lg: '10',
}

type Props = Omit<
  SVGAttributes<SVGRectElement>,
  'width' | 'height' | 'onClick' | 'children'
> & {
  /** Text for which the rectangle size is calculated. */
  text: string
  /** Top position for the g tag wrapping the rectangle and text. */
  top?: number
  /** Left position for the g tag wrapping the rectangle and text. */
  left?: number
  /** Group className. */
  groupClassName?: string
  /** Rectangle className. */
  rectClassName?: string
  /** Click handler set on the group component. */
  onClick?: (e: MouseEvent<SVGGElement>) => void
  /** Optional child element rendered as a React node or render function that receives the dimensions of the rectangle. */
  children: (dimensions: Dimensions) => ReactElement
  /** Horizontal padding applied to the rectangle */
  horizontalPadding?: number
  /** Vertical padding applied to the rectangle */
  verticalPadding?: number
  /** Size of the rounded borders. */
  rounded: 'none' | 'sm' | 'md' | 'lg'
  ref?: Ref<SVGGElement>
}

/**
 * Renders an SVG rectangle sized according to the text provided in the text property. This ensures that the text fits
 * inside the rectangle and that the correct padding is applied.
 */
export const TextRectangle = ({
  top,
  left,
  text,
  groupClassName,
  rectClassName,
  onClick,
  children,
  horizontalPadding,
  verticalPadding,
  rounded,
  ref,
  ...rectProps
}: Props) => {
  const { dimensions, svgRef } = useSvgTextSize<SVGGElement>({
    text: text || '',
  })
  const rectWidth = dimensions.width + 2 * (horizontalPadding || 0)
  const rectHeight = dimensions.height + 2 * (verticalPadding || 0)

  return (
    <Group
      top={top}
      left={left}
      innerRef={mergeRefs(svgRef, ref)}
      onClick={onClick}
      className={groupClassName}
    >
      {rectWidth > 0 && rectHeight > 0 && (
        <rect
          height={rectHeight}
          width={rectWidth}
          className={rectClassName}
          rx={RoundedBorderSize[rounded]}
          ry={RoundedBorderSize[rounded]}
          {...rectProps}
        />
      )}
      {children({ width: rectWidth, height: rectHeight })}
    </Group>
  )
}
